import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, FilePen, FileWarning, Eye, CheckCircle2, FolderOpen, ChevronRight, Building2, IndianRupee } from "lucide-react";

const STAT_CARDS = [
  { key: "draft_needed", label: "Draft Needed", Icon: FilePen, color: "text-slate-600" },
  { key: "missing_info", label: "Missing Info", Icon: FileWarning, color: "text-warning" },
  { key: "under_review", label: "Under Review", Icon: Eye, color: "text-primary" },
  { key: "finalized", label: "Finalized", Icon: CheckCircle2, color: "text-success" },
];

function PaymentsDialog() {
  const [data, setData] = useState(null);
  const open = async (o) => {
    if (o && !data) {
      try { const r = await api.get("/admin/payments"); setData(r.data); }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    }
  };
  const verify = async (id) => {
    try {
      await api.patch(`/admin/payments/${id}/verify`);
      setData((d) => ({ ...d, transactions: d.transactions.map((t) => t.id === id ? { ...t, verified: true, status: "verified" } : t) }));
      toast.success("Marked verified");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog onOpenChange={open}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="payments-btn"><IndianRupee className="h-4 w-4" />Payments</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">UPI payments — reconciliation</DialogTitle></DialogHeader>
        {!data ? <Skeleton className="h-40 w-full" /> : (
          <>
            <div className="rounded-lg bg-secondary p-3 text-sm">Reported total: <span className="font-bold text-primary">₹{(data.reported_total || 0).toLocaleString("en-IN")}</span> · {data.transactions.length} payments</div>
            <div className="space-y-2">
              {data.transactions.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No payments yet.</p>}
              {data.transactions.map((t) => (
                <div key={t.id} data-testid={`txn-${t.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-primary">{t.label} · ₹{t.amount?.toLocaleString("en-IN")}</div>
                    <div className="text-xs text-muted-foreground">{t.payer_name} · {t.payer_email} · {t.payer_phone || "no phone"}</div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">UTR: {t.utr}</div>
                  </div>
                  {t.verified
                    ? <Badge className="bg-success/10 text-success hover:bg-success/10">Verified</Badge>
                    : <Button size="sm" variant="outline" onClick={() => verify(t.id)} data-testid={`verify-${t.id}`}>Verify</Button>}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState(null);
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/cases"), api.get("/dashboard/stats")]);
      setCases(c.data);
      setStats(s.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const isStaff = user?.role === "consultant" || user?.role === "admin";

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {user?.role === "consumer" ? "My Claims" : "Case Workspace"}
          </p>
          <h1 className="mt-1 font-heading text-4xl font-bold text-primary">
            {user?.role === "consumer" ? `Hello, ${user?.name}` : "Case Dashboard"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {user?.role === "consumer"
              ? "Start a new claim challenge and we'll guide you step by step."
              : "Review, draft and finalize claim challenge letters."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && <PaymentsDialog />}
          <Button data-testid="new-case-btn" onClick={() => navigate("/cases/new")} className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
            <Plus className="h-4 w-4" /> New Case
          </Button>
        </div>
      </div>

      {/* Bento stats */}
      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        {STAT_CARDS.map(({ key, label, Icon, color }) => (
          <Card key={key} data-testid={`stat-${key}`} className="flex flex-col gap-2 rounded-md border-border p-5 shadow-sm">
            <Icon className={`h-5 w-5 ${color}`} />
            <div className="text-3xl font-bold text-primary">{stats ? stats[key] : <Skeleton className="h-8 w-10" />}</div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
          </Card>
        ))}
      </div>

      {/* Case list */}
      <div className="mt-10">
        <h2 className="font-heading text-2xl font-semibold text-primary">Cases</h2>
        <div className="mt-4 space-y-3">
          {cases === null && [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full rounded-md" />)}
          {cases && cases.length === 0 && (
            <Card className="flex flex-col items-center gap-3 rounded-md border-dashed p-12 text-center">
              <FolderOpen className="h-10 w-10 text-muted-foreground" />
              <div className="font-heading text-xl font-semibold text-primary">No cases yet</div>
              <p className="max-w-sm text-sm text-muted-foreground">Create your first case to upload documents and generate a challenge letter.</p>
              <Button data-testid="empty-new-case-btn" onClick={() => navigate("/cases/new")} className="mt-2 gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
                <Plus className="h-4 w-4" /> New Case
              </Button>
            </Card>
          )}
          {cases && cases.map((c) => (
            <Card key={c.id} data-testid={`case-row-${c.id}`} onClick={() => navigate(`/cases/${c.id}`)}
              className="group flex cursor-pointer items-center justify-between rounded-md border-border p-5 shadow-sm transition-transform duration-200 hover:-translate-y-px hover:shadow-md">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="truncate font-heading text-lg font-semibold text-primary">{c.title}</h3>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {c.insurer_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{c.insurer_name}</span>}
                  {c.claim_number && <span className="font-mono">Claim {c.claim_number}</span>}
                  {c.analysis?.case_type && <span className="capitalize">{c.analysis.case_type.replace("_", " ")}</span>}
                  {user?.role !== "consumer" && <span>{c.owner_email}</span>}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge status={c.status} />
                <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}
